import type { AuctisyncSupabase } from './client.js';

/**
 * Module #23 chunk 4 — Q&A on a lot, for the surfaces a BIDDER uses.
 *
 * WHY THIS IS SHARED. The website has let bidders ask questions since May via
 * `LotQA.tsx`, which talks to `lot_questions` inline. The native app had
 * nothing, so a bidder on a phone could not ask about a lot they were about to
 * spend money on. Porting the component would have meant a second copy of the
 * column list, the ordering and the validation rules — the divergence the
 * money-path rule exists to prevent. Both surfaces now call this.
 *
 * SCOPE IS DELIBERATELY THE BIDDER HALF ONLY. Answering and hiding are staff
 * actions that live on the tenant admin surface; the bidder app has no business
 * carrying them, and putting them in a shared module would invite a native
 * screen to call one.
 *
 * ⚠️ `asker_user_id` is never selected. It is a raw auth UUID that no bidder
 * surface renders, and `anon`'s column-scoped grant excludes it — asking for it
 * fails the whole query for a signed-out reader rather than just omitting the
 * column.
 */

export interface LotQuestion {
  id: string;
  questionText: string;
  /** Null until a member of staff answers. */
  answerText: string | null;
  answeredAt: string | null;
  createdAt: string;
}

/** Columns a bidder surface may read. Deliberately not `*`. */
const QUESTION_COLUMNS = 'id, question_text, answer_text, answered_at, created_at';

/**
 * Shortest question worth sending to a busy auctioneer. "?" is not a question,
 * and letting it through means staff triage noise rather than the bidder being
 * told immediately.
 */
export const MIN_QUESTION_LENGTH = 10;

/**
 * Long enough for a genuinely detailed question about condition or collection,
 * short enough that the column is not an essay field. Enforced client-side on
 * BOTH surfaces so the two agree on what is acceptable.
 */
export const MAX_QUESTION_LENGTH = 1000;

export type QuestionValidity =
  | { ok: true; text: string }
  | { ok: false; reason: 'empty' | 'too_short' | 'too_long'; message: string };

/**
 * Is this draft askable? Returns the trimmed text on success so callers cannot
 * accidentally send the untrimmed original.
 */
export function validateQuestion(draft: string): QuestionValidity {
  const text = draft.trim();
  if (text.length === 0) {
    return { ok: false, reason: 'empty', message: 'Type a question first.' };
  }
  if (text.length < MIN_QUESTION_LENGTH) {
    return {
      ok: false,
      reason: 'too_short',
      message: `Add a little more detail — at least ${MIN_QUESTION_LENGTH} characters.`,
    };
  }
  if (text.length > MAX_QUESTION_LENGTH) {
    return {
      ok: false,
      reason: 'too_long',
      message: `That's a bit long — keep it under ${MAX_QUESTION_LENGTH} characters.`,
    };
  }
  return { ok: true, text };
}

/** A row as the Data API returns it. Narrow, so nothing here stringifies an object. */
interface QuestionRow {
  id?: unknown;
  question_text?: unknown;
  answer_text?: unknown;
  answered_at?: unknown;
  created_at?: unknown;
}

/** Text or null — never an object coerced to "[object Object]". */
function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function toQuestion(row: QuestionRow): LotQuestion {
  return {
    id: str(row.id) ?? '',
    questionText: str(row.question_text) ?? '',
    answerText: str(row.answer_text),
    answeredAt: str(row.answered_at),
    createdAt: str(row.created_at) ?? '',
  };
}

export type FetchQuestionsResult =
  | { ok: true; questions: LotQuestion[] }
  | { ok: false; message: string };

/** Newest first — the same order the website shows. */
export async function fetchLotQuestions(
  supabase: AuctisyncSupabase,
  lotId: string,
): Promise<FetchQuestionsResult> {
  const { data, error } = await supabase
    .from('lot_questions')
    .select(QUESTION_COLUMNS)
    .eq('lot_id', lotId)
    .order('created_at', { ascending: false });

  if (error) return { ok: false, message: error.message };
  return { ok: true, questions: (data ?? []).map((r) => toQuestion(r as QuestionRow)) };
}

export type AskQuestionResult = { ok: true } | { ok: false; message: string };

/**
 * Ask a question about a lot. Validates first, so an unusable draft is refused
 * without a round trip and with the same wording on every surface.
 */
export async function askLotQuestion(
  supabase: AuctisyncSupabase,
  params: { lotId: string; userId: string; draft: string },
): Promise<AskQuestionResult> {
  const valid = validateQuestion(params.draft);
  if (!valid.ok) return { ok: false, message: valid.message };

  const { error } = await supabase.from('lot_questions').insert({
    lot_id: params.lotId,
    asker_user_id: params.userId,
    question_text: valid.text,
  });

  if (error) return { ok: false, message: error.message };
  return { ok: true };
}
